import React, { useEffect, useMemo, useState } from 'react';
import { FiGift, FiSend, FiCheck, FiX, FiCopy, FiAlertCircle } from 'react-icons/fi';
import { issuePersonalPromo, redeemPromo, revokeUserPromo } from '../../lib/promos';

// =============================================
// PromoPanel — Workbench "Promos" mode.
//   • Pick a promo (site_service_promos).
//   • Issue a single-use PERSONAL code to a user (non-circulatable).
//   • Redeem a code as a user (personal or public) — a 'service' promo grants the
//     service on redeem (user_service_grant, source=promo).
//   • Ledger of issuances/redemptions, with revoke.
// All effects run through src/lib/promos.ts; onChanged() refreshes parent state.
// =============================================

interface UserRow { user_id: number; user_type_id: number; first_name?: string; last_name?: string; screen_name?: string; email?: string; is_deleted?: number; }
interface RoleRow { user_type_id: number; user_type: string; }
interface ServiceRow { service_id: number; service_name: string; is_deleted?: number; }

interface PromoPanelProps {
  promos: any[];
  users: UserRow[];
  activeRoles: RoleRow[];
  services: ServiceRow[];
  userPromos: any[];
  onChanged: () => void | Promise<void>;
}

const APPLIES_LABEL: Record<string, string> = { plan: 'Plan', client_package: 'Client package', service: 'Service', all: 'Everything' };

const PromoPanel: React.FC<PromoPanelProps> = ({ promos, users, activeRoles, services, userPromos, onChanged }) => {
  const activePromos = useMemo(() => promos.filter(p => p.is_deleted != 1), [promos]);          // eslint-disable-line eqeqeq
  const activeUsers = useMemo(() => users.filter(u => u.is_deleted != 1).sort((a, b) => a.user_id - b.user_id), [users]); // eslint-disable-line eqeqeq

  const [selPromoId, setSelPromoId] = useState<number | ''>(activePromos[0]?.promo_id ?? '');
  const [issueUserId, setIssueUserId] = useState<number | ''>('');
  const [redeemCode, setRedeemCode] = useState('');
  const [redeemUserId, setRedeemUserId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [lastCode, setLastCode] = useState('');

  // Point at the first promo once the list loads (cache may be empty on first render).
  useEffect(() => {
    if (selPromoId === '' && activePromos.length) setSelPromoId(activePromos[0].promo_id);
  }, [activePromos, selPromoId]);

  const promo = activePromos.find(p => p.promo_id == selPromoId) || null;                        // eslint-disable-line eqeqeq

  const userLabel = (id: any) => {
    const u = activeUsers.find(x => x.user_id == id);                                            // eslint-disable-line eqeqeq
    if (!u) return id ? `#${id}` : '—';
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.screen_name || u.email || `User #${u.user_id}`;
    const role = activeRoles.find(r => r.user_type_id == u.user_type_id)?.user_type;             // eslint-disable-line eqeqeq
    return role ? `${name} — ${role}` : name;
  };
  const svcName = (id: any) => services.find(s => s.service_id == id)?.service_name || (id ? `#${id}` : ''); // eslint-disable-line eqeqeq
  const promoName = (id: any) => activePromos.find(p => p.promo_id == id)?.promo_code || `#${id}`; // eslint-disable-line eqeqeq

  const refSummary = (p: any) => {
    if (!p) return '';
    const disc = p.discount_type === 'percent' ? `${Number(p.discount_value)}% off` : `${Number(p.discount_value)} ${p.currency || ''} off`;
    const scope = p.applies_to === 'service' ? `${APPLIES_LABEL.service}: ${svcName(p.ref_id)}` : (APPLIES_LABEL[p.applies_to] || p.applies_to);
    return `${disc} · ${scope}`;
  };

  const ledger = useMemo(
    () => userPromos
      .filter(r => r.is_deleted != 1 && (selPromoId === '' || r.promo_id == selPromoId))         // eslint-disable-line eqeqeq
      .sort((a, b) => (b.user_promo_id || 0) - (a.user_promo_id || 0)),
    [userPromos, selPromoId],
  );

  const run = async (fn: () => Promise<{ ok: boolean; message: string; code?: string }>) => {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      setMsg({ ok: r.ok, text: r.message });
      if (r.ok && r.code) setLastCode(r.code);
      if (r.ok) await onChanged();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message || 'Action failed.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="rw-card pp">
      <div className="rw-field-label">Promo</div>
      <div className="pp-promo-row">
        <select className="rw-user-select" value={selPromoId} onChange={e => { setSelPromoId(e.target.value ? Number(e.target.value) : ''); setMsg(null); setLastCode(''); }}>
          {!activePromos.length && <option value="">No promos — create one in the Promos tab</option>}
          {activePromos.map(p => <option key={p.promo_id} value={p.promo_id}>{p.promo_code} — {p.promo_name}</option>)}
        </select>
        {promo && <span className="pp-promo-meta"><FiGift size={13} /> {refSummary(promo)}{Number(promo.max_redemptions) > 0 ? ` · cap ${promo.max_redemptions}` : ' · unlimited'}</span>}
      </div>

      {msg && (
        <div className={`pp-msg ${msg.ok ? 'pp-ok' : 'pp-err'}`}>
          {msg.ok ? <FiCheck size={14} /> : <FiAlertCircle size={14} />} {msg.text}
          {msg.ok && lastCode && (
            <button className="pp-copy" onClick={() => { try { navigator.clipboard?.writeText(lastCode); } catch { /* ignore */ } }} title="Copy code"><FiCopy size={12} /> {lastCode}</button>
          )}
        </div>
      )}

      <div className="pp-actions">
        <div className="pp-action">
          <div className="pp-action-title">Issue personal code <span className="rw-rail-sub">single-use, bound to one user</span></div>
          <div className="pp-action-row">
            <select className="rw-user-select" value={issueUserId} onChange={e => setIssueUserId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Pick a user…</option>
              {activeUsers.map(u => <option key={u.user_id} value={u.user_id}>{userLabel(u.user_id)}</option>)}
            </select>
            <button className="rw-assign-btn" disabled={busy || selPromoId === '' || issueUserId === ''}
              onClick={() => run(() => issuePersonalPromo(Number(selPromoId), Number(issueUserId)))}>
              <FiSend size={13} /> Issue
            </button>
          </div>
        </div>

        <div className="pp-action">
          <div className="pp-action-title">Redeem code <span className="rw-rail-sub">personal or public</span></div>
          <div className="pp-action-row">
            <input className="pp-code-input" placeholder="Enter code…" value={redeemCode} onChange={e => setRedeemCode(e.target.value)} />
            <select className="rw-user-select" value={redeemUserId} onChange={e => setRedeemUserId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">As user…</option>
              {activeUsers.map(u => <option key={u.user_id} value={u.user_id}>{userLabel(u.user_id)}</option>)}
            </select>
            <button className="rw-assign-btn" disabled={busy || !redeemCode.trim() || redeemUserId === ''}
              onClick={() => run(() => redeemPromo(redeemCode, Number(redeemUserId)))}>
              <FiCheck size={13} /> Redeem
            </button>
          </div>
        </div>
      </div>

      <div className="pp-ledger">
        <div className="rw-rail-title"><FiGift size={13} /> Redemption ledger {selPromoId !== '' && <span className="rw-rail-sub">for {promoName(selPromoId)}</span>}</div>
        {ledger.length === 0 && <span className="rw-none">Nothing issued or redeemed yet.</span>}
        {ledger.length > 0 && (
          <table className="pp-table">
            <thead>
              <tr><th>Code</th><th>Issued to</th><th>Redeemed by</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {ledger.map(r => (
                <tr key={r.user_promo_id}>
                  <td className="pp-code">{r.code}</td>
                  <td>{r.issued_to_user_id ? userLabel(r.issued_to_user_id) : <span className="pp-dim">public</span>}</td>
                  <td>{r.redeemed_by_user_id ? userLabel(r.redeemed_by_user_id) : <span className="pp-dim">—</span>}</td>
                  <td><span className={`pp-status pp-status-${r.status}`}>{r.status}</span></td>
                  <td>
                    {r.status !== 'revoked' && (
                      <button className="rw-pill-x" title="Revoke" aria-label="Revoke"
                        onClick={async () => { setBusy(true); try { await revokeUserPromo(r.user_promo_id); await onChanged(); } finally { setBusy(false); } }}>
                        <FiX size={12} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PromoPanel;
